<template>
  <v-hover>
    <template v-slot:default="{ hover }">
      <v-card class="card-container" elevation="0" v-if="story">
        <v-row class="card-row" no-gutters>
          <v-col cols="auto" class="card-col py-1 pl-2">
            <v-img
              v-if="getCoverArt(story)"
              class="cover-art"
              @load="loaded = $event"
              :src="getCoverArt(story)"
            >
              <template v-slot:placeholder>
                <div class="d-flex align-center justify-center fill-height">
                  <v-progress-circular
                    indeterminate
                    color="grey lighten-5"
                  ></v-progress-circular>
                </div>
              </template>
            </v-img>
            <CoverImagePlaceholder v-else class="cover-art" />
          </v-col>
          <v-col class="d-flex flex-column py-3 pl-5 pr-3 text-left">
            <h3 class="story-card__title mb-3">
              {{ story.name ? story.name : $t("storyHub.untitledStory") }}
            </h3>
            <StoryListInfo
              v-if="story.status"
              :status="story.status._id"
              :statusUpdatedAt="story.statusUpdatedAt"
              :summary="story.summary"
              :_id="story._id"
              :name="story.name"
              :hideBottom="hover"
              :startChapter="story.startChapter"
            />
            <v-slide-y-transition>
              <div v-if="hover" class="action-button-container d-flex">
                <StoryCityStoryActionButton
                  :to="'/storyhub/' + story._id + '/basic-info/'"
                >
                  <template v-slot:icon>
                    <v-icon size="18" color="primary">$pencil</v-icon>
                  </template>
                  <template v-slot:text>
                    {{ $t("story.editStory") }}
                  </template>
                </StoryCityStoryActionButton>
                <StoryCityStoryActionButton
                  v-if="isFeatureEnabled('analytics')"
                  to="/"
                >
                  <template v-slot:icon>
                    <img :src="require('@/assets/icons/chart.svg')" alt="" />
                  </template>
                  <template v-slot:text>
                    {{ $t("story.viewAnalytics") }}
                  </template>
                </StoryCityStoryActionButton>
                <StoryCityStoryActionButton
                  v-if="isFeatureEnabled('storefront')"
                  to="/"
                >
                  <template v-slot:icon>
                    <img :src="require('@/assets/icons/cart.svg')" alt="" />
                  </template>
                  <template v-slot:text>
                    {{ $t("story.viewStorePage") }}
                  </template>
                </StoryCityStoryActionButton>
              </div>
            </v-slide-y-transition>
          </v-col>
          <v-col cols="auto" class="py-3">
            <div class="vertical-divider"></div>
          </v-col>
          <v-col cols="auto" class="d-flex align-center pa-3">
            <v-card color="transparent" elevation="0" width="210px">
              <StoryHubChipGroup
                :totalReviewCount="story.reviews.totalCount"
                :duration="story.duration"
                :genre="story.genre"
                :ageRating="story.ageRating"
                :reviews="story.reviews"
              />
            </v-card>
          </v-col>
          <v-col cols="auto" class="py-3">
            <div class="vertical-divider"></div>
          </v-col>
          <v-col class="d-flex align-center justify-center pa-0">
            <StoryListItemStatus
              class="pa-4 pl-3"
              :icon="storyIcon"
              :status="storyStatusText"
              :updated="storyUpdatedText"
            />
          </v-col>
        </v-row>
      </v-card>
    </template>
  </v-hover>
</template>
<script>
import StoryHubChipGroup from "./StoryListChipGroup";
import StoryListInfo from "./StoryListInfo";
import StoryListItemStatus from "./StoryListItemStatus.vue";
import CoverImagePlaceholder from "@/components/CreatorTools/CoverImagePlaceholder";
import moment from "moment";
import { STORY_STATUS } from "@/const";
import StoryCityStoryActionButton from "@/components/Buttons/StoryCityStoryActionButton.vue";
import featureFlags from "@/mixins/featureFlags";

const STORY_DISPLAY_VALUES = Object.freeze({
  [STORY_STATUS.InReview]: {
    text: "storyHub.publishing.pendingReview",
    icon: "pending_review.svg",
    updatedPrefix: "storyHub.publishing.submitted",
  },
  [STORY_STATUS.Published]: {
    text: "storyHub.publishing.published",
    icon: "page.svg",
    updatedPrefix: null, // Using null here because it uses the date directly.
  },
  default: {
    // Default for any other case
    text: "storyHub.publishing.inDraft",
    icon: "in_draft.svg",
    updatedPrefix: "storyHub.publishing.created",
  },
});

export default {
  props: {
    story: Object,
  },
  mixins: [featureFlags],
  components: {
    StoryHubChipGroup,
    StoryListInfo,
    StoryListItemStatus,
    CoverImagePlaceholder,
    StoryCityStoryActionButton,
  },
  data() {
    return {
      loaded: false,
    };
  },
  methods: {
    formatDate(dateString) {
      return moment(dateString).format("MMMM D, YYYY");
    },
    getCoverArt(story) {
      if (story.coverArt && story.coverArt.contentUrl) {
        return story.coverArt.contentUrl;
      }
      return null;
    },
    getAverageRating(reviews) {
      let ratingCount = 0;
      let ratingTotal = 0;
      for (const review of reviews.edges) {
        ratingTotal = review.rating + ratingTotal;
        ratingCount++;
      }
      let average = ratingTotal / ratingCount;

      return average;
    },
  },
  computed: {
    storyDisplayValues() {
      return (
        STORY_DISPLAY_VALUES[this.story.status._id] ||
        STORY_DISPLAY_VALUES.default
      );
    },
    storyIcon() {
      return this.storyDisplayValues.icon;
    },
    storyStatusText() {
      return this.$t(this.storyDisplayValues.text);
    },
    storyUpdatedText() {
      const prefix = this.$t(this.storyDisplayValues.updatedPrefix);
      const formattedDate = this.formatDate(this.story.statusUpdatedAt);
      return prefix ? `${prefix}: ${formattedDate}` : formattedDate;
    },
  },
};
</script>
<style lang="scss" scoped>
.card-container {
  height: 216px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 20px;
  border: 1px solid var(--v-backgroundExtraLight-base);
}

.card-row {
  height: 100%;
}

.card-col {
  height: 100%;
}

.cover-art {
  border-radius: 20px;
  width: 144px;
  height: 100%;
  object-fit: cover;
}

.story-card__title h3 {
  font-size: 20px;
  font-style: normal;
  font-weight: 800;
  line-height: 22px;
  min-height: 22px;
}

.action-button-container {
  gap: 10px;
  position: absolute;
  bottom: 12px;
}

.vertical-divider {
  margin: 13px 0px;
  height: calc(100% - 26px);
}

::v-deep a {
  text-decoration: none;
}
</style>
